export type SessionUser = { identity: string; name: string; email?: string; isAdmin: boolean }
export type DashboardOrder = {
  source_order_id: string
  platform: 'shopee' | 'tiktok_shop'
  platform_order_id: string
  internal_order_id: string
  source_order_created_at: string
  source_status?: string
  order_total?: number
  product_summary?: Array<{ sku?: string; name?: string; quantity?: number }>
  verification_status: 'pending' | 'verified' | 'needs_review' | 'rejected'
  public_code?: string
  customer_name_masked?: string
  customer_phone_masked?: string
  registered_at?: string
  registration_id?: string
}
export type ProcessingRun = {
  run_id: string
  registration_id?: string
  source_order_id?: string
  session_key: string
  platform?: 'shopee' | 'tiktok_shop'
  platform_order_id?: string
  customer_phone_masked?: string
  outcome: 'verified' | 'needs_review' | 'needs_input' | 'no_match' | 'failed' | 'duplicate'
  is_duplicate?: boolean
  duration_ms?: number
  started_at: string
  completed_at?: string
  current_step?: string
  current_message?: string
  event_count?: number
  source_channel?: 'customer_web' | 'pilot_mcp'
  tester_id?: string
  expected_outcome?: 'verified' | 'needs_review' | 'needs_input' | 'no_match' | 'failed' | 'duplicate'
  client_name?: string
  expectation_mismatch?: boolean
  feedback_id?: string
  feedback_verdict?: 'correct' | 'incorrect' | 'unclear'
  feedback_bug_type?: 'ocr' | 'order_matching' | 'phone_matching' | 'result_copy' | 'latency' | 'system' | 'other'
  feedback_note?: string
  feedback_created_at?: string
}
export type DashboardPayload = {
  stats: Record<string, number | string | null>
  orders: DashboardOrder[]
  runs: ProcessingRun[]
}
export type ProcessingEvent = {
  event_id: string
  run_id: string
  sequence_no: number
  type?: string
  step: string
  status: string
  message?: string
  elapsed_ms: number
  created_at: string
}
export type DashboardDetail = {
  order?: Record<string, unknown>
  registration?: Record<string, unknown> & { ocr_fields?: Record<string, unknown> }
  runs: Array<Record<string, unknown>>
  events: ProcessingEvent[]
  feedback: Array<Record<string, unknown>>
}
export type VerificationProgress = {
  type: 'progress' | 'complete' | 'error'
  step: 'check' | 'upload' | 'ocr' | 'verify' | 'complete' | 'error'
  status: string
  message: string
  elapsedMs: number
  publicCode?: string
  duplicate?: boolean
  alreadyVerified?: boolean
}
export type VerificationResult = Pick<VerificationProgress, 'message' | 'status' | 'elapsedMs' | 'publicCode' | 'duplicate' | 'alreadyVerified'>

const TOKEN_KEY = 'gearvn_warranty_access_token'
const USER_KEY = 'gearvn_warranty_user'
const AUTH_URL = 'https://accounts.gearvn.xyz'
let refreshInFlight: Promise<SessionUser | null> | null = null

export function getToken() { return localStorage.getItem(TOKEN_KEY) ?? '' }
export function getStoredUser(): SessionUser | null {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null') as SessionUser | null }
  catch { return null }
}
function saveSession(payload: { accessToken: string; user: SessionUser }) {
  localStorage.setItem(TOKEN_KEY, payload.accessToken)
  localStorage.setItem(USER_KEY, JSON.stringify(payload.user))
  return payload.user
}
export function clearSession() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
}
export function getSsoLoginUrl() {
  return `${AUTH_URL}/login?redirect_uri=${encodeURIComponent(`${window.location.origin}/auth/sso/callback`)}`
}
export async function exchangeSso(code: string) {
  const response = await fetch('/api/auth/callback', { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ code }) })
  const body = await response.json() as { accessToken: string; user: SessionUser; error?: string }
  if (!response.ok) throw new Error(body.error || 'Không xác thực được SSO')
  return saveSession(body)
}
export async function refreshSession() {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = (async () => {
    const response = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' })
    if (response.status === 401 || response.status === 403) { clearSession(); return null }
    const body = await response.json().catch(() => ({})) as { accessToken?: string; user?: SessionUser; error?: string }
    if (!response.ok || !body.accessToken || !body.user) throw new Error(body.error || 'Dịch vụ SSO đang bận. Phiên đăng nhập vẫn được giữ để thử lại.')
    return saveSession({ accessToken: body.accessToken, user: body.user })
  })().finally(() => { refreshInFlight = null })
  return refreshInFlight
}
export async function logout() {
  await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => undefined)
  clearSession()
  window.location.href = '/admin'
}
export function normalizeCustomerPhone(value: string) {
  let digits = value.replace(/\D/g, '')
  if (digits.startsWith('84') && (digits.length === 11 || digits.length === 12)) digits = `0${digits.slice(2)}`
  return /^0[0-9]{9,10}$/.test(digits) ? digits : ''
}
export async function fetchPublicConfig() {
  const response = await fetch('/api/config')
  if (!response.ok) return { turnstileSiteKey: '' }
  return response.json() as Promise<{ turnstileSiteKey: string }>
}
export async function submitVerification(
  input: { file: File; platform: string; imei: string; customerPhone: string; sessionId: string; startedAt: number; turnstileToken?: string },
  onProgress?: (event: VerificationProgress) => void,
) {
  const form = new FormData()
  form.set('file', input.file)
  form.set('platform', input.platform)
  form.set('imei', input.imei)
  form.set('customerPhone', input.customerPhone)
  form.set('sessionId', input.sessionId)
  form.set('startedAt', String(input.startedAt))
  form.set('website', '')
  if (input.turnstileToken) form.set('turnstileToken', input.turnstileToken)
  const response = await fetch('/api/verify', { method: 'POST', headers: { accept: 'application/x-ndjson' }, body: form })
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string }
    throw new Error(body.error || 'Không xử lý được ảnh. Vui lòng thử lại.')
  }
  if (!response.body) throw new Error('Trình duyệt không hỗ trợ nhận tiến trình xử lý.')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let completed: VerificationProgress | null = null
  const consume = (line: string) => {
    if (!line.trim()) return null
    const event = JSON.parse(line) as VerificationProgress
    onProgress?.(event)
    if (event.type === 'error') throw new Error(event.message)
    return event
  }
  while (true) {
    const { done, value } = await reader.read()
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done })
    const lines = buffer.split('\n')
    buffer = lines.pop() || ''
    for (const line of lines) {
      const event = consume(line)
      if (event?.type === 'complete') completed = event
    }
    if (done) break
  }
  const lastEvent = consume(buffer)
  if (lastEvent?.type === 'complete') completed = lastEvent
  if (!completed) throw new Error('Hệ thống chưa trả kết quả xác thực.')
  return {
    message: completed.message, status: completed.status, publicCode: completed.publicCode,
    elapsedMs: completed.elapsedMs, duplicate: completed.duplicate, alreadyVerified: completed.alreadyVerified,
  } satisfies VerificationResult
}
export async function fetchDashboard(params: URLSearchParams) {
  const response = await fetchWithSession(`/api/admin/dashboard?${params}`)
  const body = await response.json() as DashboardPayload & { error?: string }
  if (!response.ok) throw new Error(body.error || 'Không tải được dashboard.')
  return body
}
export async function fetchDashboardDetail(params: { sourceOrderId?: string; registrationId?: string; runId?: string }) {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) if (value) query.set(key, value)
  const response = await fetchWithSession(`/api/admin/detail?${query}`)
  const body = await response.json() as DashboardDetail & { error?: string }
  if (!response.ok) throw new Error(body.error || 'Không tải được chi tiết xử lý.')
  return body
}

async function fetchWithSession(input: RequestInfo | URL, init: RequestInit = {}) {
  const request = () => {
    const headers = new Headers(init.headers)
    headers.set('Authorization', `Bearer ${getToken()}`)
    return fetch(input, { ...init, credentials: 'include', headers })
  }
  let response = await request()
  if (response.status !== 401) return response
  const user = await refreshSession()
  if (!user) throw new Error('Phiên SSO đã hết hạn. Vui lòng đăng nhập lại.')
  response = await request()
  if (response.status === 401) { clearSession(); throw new Error('Phiên SSO đã hết hạn. Vui lòng đăng nhập lại.') }
  return response
}
